import React, { useState, useEffect } from 'react';
import styles from '../styles/MTNDigitalServices.module.scss';
import { notify } from '../utils/notifications';
import axios from 'axios';
import { Trans, useTranslation } from 'react-i18next';

const ContactForm = () => {

  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    cellphone: '',
    subject: '',
    message: '',
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Removed unused state variable: 'submissionStatus'
  const [messageWordCount, setMessageWordCount] = useState(0);
  const MAX_MESSAGE_WORDS = 500;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (validationErrors[name]) {
      setValidationErrors({ ...validationErrors, [name]: '' });
    }
    if (name === 'message') {
      const words = value.trim().split(/\s+/).filter(word => word.length > 0);
      setMessageWordCount(words.length);
    }
  };


  const validateForm = () => {
    let errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d+$/;

    if (!formData.name.trim()) errors.name = t('contactForm.validationName');
    if (!formData.email.trim() || !emailRegex.test(formData.email)) errors.email = t('contactForm.validationEmail');
    if (!formData.cellphone.trim() || !phoneRegex.test(formData.cellphone)) errors.cellphone = t('contactForm.validationCellphone');
    if (!formData.subject.trim()) errors.subject = t('contactForm.validationSubject');
    if (!formData.message.trim()) errors.message = t('contactForm.validationMessage');
    else if (messageWordCount > MAX_MESSAGE_WORDS) errors.message = t('contactForm.validationMessage', {MAX_MESSAGE_WORDS: MAX_MESSAGE_WORDS});

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    const isValid = Object.values(formData).every(value => value.trim() !== '') && messageWordCount <= MAX_MESSAGE_WORDS;
    setIsFormValid(isValid);
  }, [formData, messageWordCount]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isValid = validateForm();
    if (!isValid) return;
  
    setIsSubmitting(true);
  
    try {
      const response = await axios.post(`/api/getIntouch`, formData);
      const responseData = response.data;
      
      if (responseData.status === 'success') {
          notify('success', 'Success!');
          setFormData({ name: '', email: '', cellphone: '', subject: '', message: '' });
          setMessageWordCount(0);
      }
      
    } catch (error) {
      console.error('Submission error:', error);
      const { message } = error.response?.data || {};
      
      if (message) {
        notify('error', `Error: ${message}`);
      } else {
        notify(
          'error',
          'Error: Oops! Something went wrong on our side. Please try again later.'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
};

  return (
    <form className={styles.contactForm} onSubmit={handleSubmit}>
      {/* Name */}
      <div>
        <div className={`${styles.inputGroup} ${formData.name ? styles.hasValue : ''}`}>
          <label htmlFor="name" className={styles.label}><Trans i18nKey="contactForm.labelName" /> <span className={styles.required}>*</span></label>
          <input
            type="text" id="name" name="name"
            className={`${styles.input} ${validationErrors.message ? styles.hasError : ''}`}  required
            value={formData.name}
            onChange={handleInputChange} onBlur={validateForm}
          />
        </div>
        {validationErrors.name && (
          <div className={styles.errorText}>
            <img src="/images/error.svg" alt="Error" className={styles.errorIcon} />
            <span>{validationErrors.name}</span>
          </div>
        )}
      </div>

      {/* Email */}
      <div>
        <div className={`${styles.inputGroup} ${formData.email ? styles.hasValue : ''}`}>
          <label htmlFor="email" className={styles.label}><Trans i18nKey="contactForm.labelEmail" /> <span className={styles.required}>*</span></label>
          <input
            type="email" id="email" name="email"
            className={`${styles.input} ${validationErrors.message ? styles.hasError : ''}`} required
            
            value={formData.email}
            onChange={handleInputChange} onBlur={validateForm}
          />
        </div>
        {validationErrors.email && (
          <div className={styles.errorText}>
            <img src="/images/error.svg" alt="Error" className={styles.errorIcon} />
            <span>{validationErrors.email}</span>
          </div>
        )}
      </div>

      {/* Cellphone */}
      <div>
        <div className={`${styles.inputGroup} ${formData.cellphone ? styles.hasValue : ''}`}>
          <label htmlFor="cellphone" className={styles.label}><Trans i18nKey="contactForm.labelCellphoneNumber" /> <span className={styles.required}>*</span></label>
          <input
            type="tel" id="cellphone" name="cellphone"
            className={`${styles.input} ${validationErrors.message ? styles.hasError : ''}`} required
            value={formData.cellphone}
            onChange={handleInputChange} onBlur={validateForm}
          />
        </div>
        {validationErrors.cellphone && (
          <div className={styles.errorText}>
            <img src="/images/error.svg" alt="Error" className={styles.errorIcon} />
            <span>{validationErrors.cellphone}</span>
          </div>
        )}
      </div>

      {/* Subject */}
      <div>
        <div className={`${styles.inputSelectGroup} ${formData.subject ? styles.hasValue : ''}`}>
          <label htmlFor="subject" className={styles.label}><Trans i18nKey="contactForm.labelSubject" /></label>
          <select
            id="subject" name="subject"
            className={`${styles.select} ${validationErrors.message ? styles.hasError : ''}`}  required
            value={formData.subject}
            onChange={handleInputChange} onBlur={validateForm}
          >
            {/* Empty hidden placeholder option: shows blank initially */}
            <option value="" hidden></option>
            <option value="select_an_option" disabled>{t('contactForm.selectOption0')}</option>
            <option value="general_feedback">{t('contactForm.selectOption1')}</option>
            <option value="technical_support">{t("contactForm.selectOption2")}</option>
            <option value="billing_question">{t("contactForm.selectOption3")}</option>
            <option value="report_user">{t("contactForm.selectOption4")}</option>
            <option value="inappropriate_material">{t("contactForm.selectOption5")}</option>
            <option value="suggest_a_feature">{t("contactForm.selectOption6")}</option>
            <option value="delete_account">{t("contactForm.selectOption7")}</option>
          </select>
        </div>
        {validationErrors.subject && (
          <div className={styles.errorText}>
            <img src="/images/error.svg" alt="Error" className={styles.errorIcon} />
            <span>{validationErrors.subject}</span>
          </div>
        )}
      </div>

      {/* Message */}
      <div>
        <div className={`${styles.inputTextAreaGroup} ${formData.message ? styles.hasValue : ''}`}>
          <label htmlFor="message" className={styles.label}>Message <span className={styles.required}>*</span></label>
          <textarea
            id="message" name="message"
            className={`${styles.textareaInput} ${validationErrors.message ? styles.hasError : ''}`} rows="5" required
            value={formData.message}
            onChange={handleInputChange} onBlur={validateForm}
          />
          <p className={styles.wordCount}>{messageWordCount} / {MAX_MESSAGE_WORDS} words</p>
        </div>
        {validationErrors.message && (
          <div className={styles.errorText}>
            <img src="/images/error.svg" alt="Error" className={styles.errorIcon} />
            <span>{validationErrors.message}</span>
          </div>
        )}
      </div>

      <button
        type="submit"
        className={isFormValid && !isSubmitting ? styles.submitButton : styles.submitButtonDisabled}
        disabled={!isFormValid || isSubmitting}
      >
        {isSubmitting ? 'Sending...' : t('contactForm.send')}
      </button>

    </form>
  );
};

export default ContactForm;
